<script>
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import isFunction from 'lodash/isFunction';
import MaskedInput from 'vue-text-mask';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import flatPickr from 'vue-flatpickr-component';
import DriverModel from '@models/Driver';
import LocationModel from '@models/Location';
import BlockingContainer from '@components/BlockingContainer';
import FileWrapper from '@components/FileWrapper';
import SingleImageCrop from '@components/SingleImageCrop';
import FileDownloadLink from '@components/FileDownloadLink';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';
import EntityMultiselectMultiple from '@components/EntityMultiselectMultiple';
import _find from 'lodash/find';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
    MaskedInput,
    EntityMultiselectMultiple,
    FileWrapper,
    SingleImageCrop,
    FileDownloadLink,
  },
  mixins: [editFormChange, datePickerUtility],
  props: {
    value: {
      type: Object,
      default: null,
      required: false,
    },
    initialLocations: {
      type: Array,
      required: true,
      default: () => [],
    },
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      user: {
        creditor_number: null,
        first_name: null,
        last_name: null,
        email: null,
        password: null,
        sex: 1,
        dob: null,
        mobile_number: null,
        street: null,
        postal_code: null,
        town: null,
        country: null,
        nationality: null,
        bank_name: null,
        iban: null,
        bic: null,
        locations: [],
        roles: [],
        id_card: null,
        driving_license: null,
        transport_license: null,
        id_card_number: null,
        driving_license_number: null,
        transport_license_date: null,
        verified: false,
        invoice_issuer: 0,
        remarks: '',
        avatar: {
          id: Math.random(),
          original: null,
          meta_data: {
            height: 0,
            scaleX: 1,
            scaleY: 1,
            width: 0,
            x: 0,
            y: 0,
          },
        },
      },
      avatarUploadShow: false,
      locationModel: LocationModel,
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
      dOBDateConfig: (state) =>
        Object.assign({}, state.common.dateConfig, {
          maxDate: DateTime.local()
            .minus({ years: 18 })
            .toFormat('yyyy-LL-dd'),
        }),
    }),
    isAdmin() {
      return !!_find(this.user.roles, { name: 'admin' });
    }
  },
  mounted() {
    if (this.value) {
      this.user = Object.assign(this.user, this.value);
      this.$nextTick(() => {
        this.initChangeWatcher(['user'], this.validate);
      });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('driver loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      if (this.$route.params.id) {
        this.user.id = this.$route.params.id;
        if (!this.user.password) delete this.user.password;
      }

      // remove unnecesarry stuff from user object
      // remove leaves key if exists
      delete this.user.leaves;
      new DriverModel(
        Object.assign({}, this.user, {
          id_card: Array.isArray(this.user.id_card) ? this.user.id_card[0] : this.user.id_card,
          driving_license: Array.isArray(this.user.driving_license) ? this.user.driving_license[0] : this.user.driving_license,
          transport_license: Array.isArray(this.user.transport_license) ? this.user.transport_license[0] : this.user.transport_license,
        })
      )
        .save()
        .then((response) => {
          this.$emit('submitted', callback && isFunction(callback));
          if (callback && isFunction(callback)) callback();
          this.$wait.end('driver loading');
        })
        .catch(() => this.$wait.end('driver loading'));
    },
    metaUpdateImage(entity, meta) {
      this.$set(this.user[entity], 'meta_data', meta);
    },
    doRemoveAdmin() {
      this.$wait.start('driver loading');
      this.$http
        .put(`admin/drivers/${this.user.id}/remove-admin`)
        .then((response) => {
            this.user.roles = this.user.roles.filter(role => role.name !== 'admin');
            this.$wait.end('driver loading');
        })
        .catch(() => this.$wait.end('driver loading'));
    },
    removeAdmin() {
      this.$dialog
          .confirm({
            title: this.$t('alerts.warning'),
            body: this.$t('alerts.remove_driver_admin'),})
          .then(() => this.doRemoveAdmin())
          .catch();
    },    
    doMakeAdmin() {
      this.$wait.start('driver loading');
      this.$http
        .put(`admin/drivers/${this.user.id}/make-admin`)
        .then((response) => {
            this.user.roles.push({name: 'admin'});
            this.$wait.end('driver loading');
        })
        .catch(() => this.$wait.end('driver loading'));
    },
    makeAdmin() {
      this.$dialog
          .confirm({
            title: this.$t('alerts.warning'),
            body: this.$t('alerts.make_driver_admin'),})
          .then(() => this.doMakeAdmin())
          .catch();
    },
    toggleAdmin(isChecked) {
      if (isChecked) {
        this.makeAdmin();
      } else {
        this.removeAdmin();
      }
    }
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('driver loading')"></BlockingContainer>
    <b-card
      :border-variant="!errors.items.length ? 'dark' : 'danger'"
      :header="$t('titles.edit_driver')"
      header-tag="header"
      :header-bg-variant="!errors.items.length ? 'dark' : 'danger'"
      header-text-variant="white"
      class="mb-4"
    >    
      <b-form @submit.prevent="validate">
        <b-form-group :label="`${$t('fields.creditor_number')}:`">
          <b-form-input
            v-model="user.creditor_number"
            v-validate="'digits:5'"
            data-vv-value-path="localValue"
            data-vv-name="creditor_number"
            :data-vv-as="$t('fields.creditor_number')"
            type="text"
            :state="errors.has('creditor_number') ? false : null"
            aria-describedby="input-feedback-creditor_number"
            :placeholder="$t('placeholders.creditor_number')"
          >
          </b-form-input>

          <b-form-invalid-feedback id="input-feedback-creditor_number">
            <template v-show="errors.has('creditor_number')">{{ errors.first('creditor_number') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-row>
          <b-col sm="6">
            <b-form-group :label="`${$t('fields.first_name')}:`">
              <b-form-input
                id="first_name"
                v-model="user.first_name"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="first_name"
                :data-vv-as="$t('fields.first_name')"
                type="text"
                :state="errors.has('first_name') ? false : null"
                aria-describedby="input-feedback-first_name"
                :placeholder="$t('placeholders.first_name')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-first_name">
                <template v-show="errors.has('first_name')">{{ errors.first('first_name') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group :label="`${$t('fields.last_name')}:`">
              <b-form-input
                id="last_name"
                v-model="user.last_name"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="last_name"
                :data-vv-as="$t('fields.last_name')"
                type="text"
                :state="errors.has('last_name') ? false : null"
                aria-describedby="input-feedback-last_name"
                :placeholder="$t('placeholders.last_name')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-last_name">
                <template v-show="errors.has('last_name')">{{ errors.first('last_name') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <SingleImageCrop
              v-if="avatarUploadShow"
              v-model="user.avatar.original"
              :meta-data="user.avatar.meta_data"
              :init="true"
              :clear-button-container="$style.clearButtonContainer"
              dropzone-class="fileUploadZoneSmall"
              image-resize-height="155px"
              clear-button="btn-sm"
              @meta-update="metaUpdateImage('avatar', ...arguments)"
            ></SingleImageCrop>
            <template v-else>
              <img
                v-if="!user.avatar.original"
                class="img-thumbnail mx-auto d-block rounded-circle"
                :class="$style.avatarImg"
                src="@assets/images/none.png"
                alt="Avatar"
                @click="avatarUploadShow = true"
              />
              <img
                v-else
                :key="user.avatar.large"
                class="img-thumbnail mx-auto d-block rounded-circle"
                :class="$style.avatarImg"
                :src="user.avatar.large"
                alt="Avatar"
                @click="avatarUploadShow = true"
              />
            </template>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col sm="4">
            <b-form-group :label="`${$t('fields.email')}:`">
              <b-form-input
                id="email"
                v-model="user.email"
                v-validate="'required|email|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="email"
                :data-vv-as="$t('fields.email')"
                type="text"
                :state="errors.has('email') ? false : null"
                aria-describedby="input-feedback-email"
                :placeholder="$t('placeholders.email')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-email">
                <template v-show="errors.has('email')">{{ errors.first('email') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col sm="4">
            <b-form-group :label="`${$t('fields.password')}:`">
              <input
                ref="password"
                v-model="user.password"
                v-validate="`${!loadedId ? 'required|' : ''}min:8|max:255|verify_password`"
                name="password"
                class="form-control"
                data-vv-name="password"
                :data-vv-as="$t('fields.password')"
                type="password"
                :class="errors.has('password') ? 'is-invalid' : null"
                aria-describedby="input-feedback-password"
                :placeholder="$t('placeholders.password')"
              />

              <b-form-invalid-feedback id="input-feedback-password">
                <template v-show="errors.has('password')">{{ errors.first('password') }}</template>
              </b-form-invalid-feedback>
              <!-- <b-form-text v-if="loadedId">{{ $t('infos.password_edit') }}</b-form-text> -->
            </b-form-group>
          </b-col>
          <b-col sm="4">
            <b-form-group :label="`${$t('fields.gender')}:`">
              <b-form-radio-group
                v-model="user.sex"
                v-validate="'required'"
                data-vv-value-path="localValue"
                data-vv-name="sex"
                :data-vv-as="$t('fields.gender')"
                aria-describedby="input-feedback-sex"
              >
                <b-form-radio :value="1">{{ $t('fields.sex.1') }}</b-form-radio>
                <b-form-radio :value="2">{{ $t('fields.sex.2') }}</b-form-radio>
                <b-form-radio :value="3">{{ $t('fields.sex.3') }}</b-form-radio>
              </b-form-radio-group>
              <b-form-invalid-feedback id="input-feedback-sex">
                <template v-show="errors.has('sex')">{{ errors.first('sex') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col>
            <b-form-group :label="`${$t('fields.dob')}:`">
              <flat-pickr
                v-model="user.dob"
                v-validate="''"
                data-vv-name="dob"
                :data-vv-as="$t('fields.dob')"
                aria-describedby="input-feedback-dob"
                :config="dOBDateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('dob') }"
                :placeholder="$t('placeholders.date')"
                @on-close="closeCalendar('user.dob', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-dob">
                <template v-show="errors.has('dob')"
                  >{{ errors.first('dob') }}
                </template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="`${$t('fields.locations')}:`">
              <EntityMultiselectMultiple
                v-model="user.locations"
                :initial-value="initialLocations"
                :placeholder="$t('placeholders.locations')"
                sort="name"
                :model="locationModel"
              >
              </EntityMultiselectMultiple>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group :label="`${$t('fields.mobile_number')}:`">
          <b-form-input
            v-model="user.mobile_number"
            v-validate="'required|max:255'"
            data-vv-value-path="localValue"
            data-vv-name="mobile_number"
            :data-vv-as="$t('fields.mobile_number')"
            type="text"
            :state="errors.has('mobile_number') ? false : null"
            aria-describedby="input-feedback-mobile_number"
            :placeholder="$t('placeholders.mobile_number')"
          >
          </b-form-input>

          <b-form-invalid-feedback id="input-feedback-mobile_number">
            <template v-show="errors.has('mobile_number')">{{ errors.first('mobile_number') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-row>
          <b-col>
            <b-form-group :label="`${$t('fields.street')}:`">
              <b-form-input
                id="street"
                v-model="user.street"
                v-validate="'max:65000'"
                data-vv-value-path="localValue"
                data-vv-name="street"
                :data-vv-as="$t('fields.street')"
                type="text"
                :state="errors.has('street') ? false : null"
                aria-describedby="input-feedback-street"
                :placeholder="$t('placeholders.street')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-street">
                <template v-show="errors.has('street')">{{ errors.first('street') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="`${$t('fields.postal_code')}:`">
              <b-form-input
                id="postal_code"
                v-model="user.postal_code"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="postal_code"
                :data-vv-as="$t('fields.postal_code')"
                type="text"
                :state="errors.has('postal_code') ? false : null"
                aria-describedby="input-feedback-postal_code"
                :placeholder="$t('placeholders.postal_code')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-postal_code">
                <template v-show="errors.has('postal_code')">{{ errors.first('postal_code') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col>
            <b-form-group :label="`${$t('fields.town')}:`">
              <b-form-input
                id="town"
                v-model="user.town"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="town"
                :data-vv-as="$t('fields.town')"
                type="text"
                :state="errors.has('town') ? false : null"
                aria-describedby="input-feedback-town"
                :placeholder="$t('placeholders.town')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-town">
                <template v-show="errors.has('town')">{{ errors.first('town') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="`${$t('fields.country')}:`">
              <b-form-input
                id="country"
                v-model="user.country"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="country"
                :data-vv-as="$t('fields.country')"
                type="text"
                :state="errors.has('country') ? false : null"
                aria-describedby="input-feedback-country"
                :placeholder="$t('placeholders.country')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-country">
                <template v-show="errors.has('country')">{{ errors.first('country') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group :label="`${$t('fields.nationality')}:`">
          <b-form-input
            id="nationality"
            v-model="user.nationality"
            v-validate="'max:255'"
            data-vv-value-path="localValue"
            data-vv-name="nationality"
            :data-vv-as="$t('fields.nationality')"
            type="text"
            :state="errors.has('nationality') ? false : null"
            aria-describedby="input-feedback-nationality"
            :placeholder="$t('placeholders.nationality')"
          >
          </b-form-input>

          <b-form-invalid-feedback id="input-feedback-nationality">
            <template v-show="errors.has('nationality')">{{ errors.first('nationality') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <template v-slot:label class="col-form-legend col-12">
            {{ $t('fields.id_card_number') }}:
            <span class="near-label-info text-secondary" @click.stop>
              <FontAwesomeIcon id="idCardNumberPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
              <b-popover target="idCardNumberPopover" triggers="focus" placement="rightbottom">
                {{ $t('infos.id_card_number') }}
              </b-popover>
            </span>
          </template>
          <b-form-input
            v-model="user.id_card_number"
            v-validate="'max:255'"
            data-vv-value-path="localValue"
            data-vv-name="id_card_number"
            :data-vv-as="$t('fields.id_card_number')"
            type="text"
            :state="errors.has('id_card_number') ? false : null"
            aria-describedby="input-feedback-id_card_number"
            :placeholder="$t('placeholders.id_card_number')"
          >
          </b-form-input>

          <b-form-invalid-feedback id="input-feedback-id_card_number">
            <template v-show="errors.has('id_card_number')">{{ errors.first('id_card_number') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="`${$t('fields.id_card')}:`">
          <FileDownloadLink v-if="user.id_card && !Array.isArray(user.id_card)" :file="user.id_card" @remove="user.id_card = null" />
          <FileWrapper
            v-else
            v-model="user.id_card"
            v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
            :data-vv-as="$t('fields.id_card')"
            :state="errors.has('id_card') ? false : null"
            :placeholder="$t('placeholders.id_card')"
            accept="image/*,application/pdf"
            aria-describedby="input-feedback-id_card"
            name="id_card"
          >
          </FileWrapper>

          <div v-show="errors.has('id_card')" class="invalid-feedback d-block mt-1">
            {{ errors.first('id_card') }}
          </div>
        </b-form-group>

        <b-form-group :label="`${$t('fields.driving_license_number')}:`">
          <b-form-input
            v-model="user.driving_license_number"
            v-validate="'max:255'"
            data-vv-value-path="localValue"
            data-vv-name="driving_license_number"
            :data-vv-as="$t('fields.driving_license_number')"
            type="text"
            :state="errors.has('driving_license_number') ? false : null"
            aria-describedby="input-feedback-driving_license_number"
            :placeholder="$t('placeholders.driving_license_number')"
          >
          </b-form-input>

          <b-form-invalid-feedback id="input-feedback-driving_license_number">
            <template v-show="errors.has('driving_license_number')">{{ errors.first('driving_license_number') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="`${$t('fields.driving_license')}:`">
          <FileDownloadLink
            v-if="user.driving_license && !Array.isArray(user.driving_license)"
            :file="user.driving_license"
            @remove="user.driving_license = null"
          />
          <FileWrapper
            v-else
            v-model="user.driving_license"
            v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
            :data-vv-as="$t('fields.driving_license')"
            :state="errors.has('driving_license') ? false : null"
            :placeholder="$t('placeholders.driving_license')"
            accept="image/*,application/pdf"
            aria-describedby="input-feedback-driving_license"
            name="driving_license"
          >
          </FileWrapper>

          <div v-show="errors.has('driving_license')" class="invalid-feedback d-block mt-1">
            {{ errors.first('driving_license') }}
          </div>
        </b-form-group>

        <b-form-group>
          <template v-slot:label class="col-form-legend col-12">
            {{ $t('fields.transport_license_date') }}:
            <span class="near-label-info text-secondary" @click.stop>
              <FontAwesomeIcon id="transportLicenseDatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
              <b-popover target="transportLicenseDatePopover" triggers="focus" placement="rightbottom">
                {{ $t('infos.transport_license_date') }}
              </b-popover>
            </span>
          </template>
          <flat-pickr
                  v-model="user.transport_license_date"
                  v-validate="''"
                  data-vv-value-path="localValue"
                  data-vv-name="transport_license_date"
                  :data-vv-as="$t('fields.transport_license_date')"
                  aria-describedby="input-feedback-transport_license_date"
                  :config="dateConfig"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('transport_license_date') }"
                  :placeholder="$t('placeholders.transport_license_date')"
                  @on-close="closeCalendar('user.transport_license_date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)">
          </flat-pickr>
          <b-form-invalid-feedback id="input-feedback-transport_license_date">
            <template v-show="errors.has('transport_license_date')">{{ errors.first('transport_license_date') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="`${$t('fields.transport_license')}:`">
          <FileDownloadLink
            v-if="user.transport_license && !Array.isArray(user.transport_license)"
            :file="user.transport_license"
            @remove="user.transport_license = null"
          />
          <FileWrapper
            v-else
            v-model="user.transport_license"
            v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
            :data-vv-as="$t('fields.transport_license')"
            :state="errors.has('transport_license') ? false : null"
            :placeholder="$t('placeholders.transport_license')"
            accept="image/*,application/pdf"
            aria-describedby="input-feedback-transport_license"
            name="transport_license"
          >
          </FileWrapper>

          <div v-show="errors.has('transport_license')" class="invalid-feedback d-block mt-1">
            {{ errors.first('transport_license') }}
          </div>
        </b-form-group>

        <b-form-group :label="`${$t('fields.bank_name')}:`">
          <b-form-input
            id="bank_name"
            v-model="user.bank_name"
            v-validate="'max:255'"
            data-vv-value-path="localValue"
            data-vv-name="bank_name"
            :data-vv-as="$t('fields.bank_name')"
            type="text"
            :state="errors.has('bank_name') ? false : null"
            aria-describedby="input-feedback-bank_name"
            :placeholder="$t('placeholders.bank_name')"
          >
          </b-form-input>

          <b-form-invalid-feedback id="input-feedback-bank_name">
            <template v-show="errors.has('bank_name')">{{ errors.first('bank_name') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-row>
          <b-col sm="7">
            <b-form-group :label="`${$t('fields.iban')}:`">
              <MaskedInput
                v-model="user.iban"
                v-validate="'max:27'"
                data-vv-value-path="localValue"
                data-vv-name="iban"
                :data-vv-as="$t('fields.iban')"
                type="text"
                name="phone"
                class="form-control"
                :mask="[
                  /[A-Z]/i,
                  /[A-Z]/i,
                  /\d/,
                  /\d/,
                  ' ',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  ' ',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  ' ',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  ' ',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  ' ',
                  /\d/,
                  /\d/,
                ]"
                :guide="true"
                :pipe="(conformedValue) => conformedValue.toUpperCase()"
                placeholder-char="_"
                aria-describedby="input-feedback-iban"
                :class="errors.has('iban', 'profile') ? 'is-invalid' : null"
                placeholder="DE12 3456 7890 1234 5678 91"
              >
              </MaskedInput>

              <b-form-invalid-feedback id="input-feedback-iban">
                <template v-show="errors.has('iban')">{{ errors.first('iban') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.bic') }}:
                <span class="near-label-info text-secondary" @click.stop>
                  <FontAwesomeIcon id="bicPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                  <b-popover target="bicPopover" triggers="focus" placement="rightbottom">
                    {{ $t('infos.bic') }}
                  </b-popover>
                </span>
              </template>
              <b-form-input
                id="bic"
                v-model="user.bic"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="bic"
                :data-vv-as="$t('fields.bic')"
                type="text"
                :state="errors.has('bic') ? false : null"
                aria-describedby="input-feedback-bic"
                :placeholder="$t('placeholders.bic')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-bic">
                <template v-show="errors.has('bic')">{{ errors.first('bic') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-button type="submit" :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
          {{ $t('actions.submit') }}
        </b-button>
      </b-form>
    </b-card>
    <b-card
      v-if="loadedId"
      :header="$t('titles.processing')"
      header-tag="header"
      header-bg-variant="dark"
      header-text-variant="white"
      border-variant="dark"
      class="mb-4"
    >
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.remarks') }}:
        </template>
        <b-form-input
          id="remarks"
          v-model="user.remarks"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="remarks"
          :data-vv-as="$t('fields.remarks')"
          type="text"
          :state="errors.has('remarks') ? false : null"
          aria-describedby="input-feedback-remarks"
          :placeholder="$t('placeholders.remarks')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-remarks">
          <template v-show="errors.has('remarks')">{{ errors.first('remarks') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
                v-model="user.invoice_issuer"
                :unchecked-value="0"
                :value="1"
                switch
        > {{ $t('fields.invoice_issuer') }} </b-form-checkbox>
      </b-form-group>        
      <b-form-group>
        <b-form-checkbox
                v-model="user.verified"
                switch
        > {{ $t('fields.verified') }} </b-form-checkbox>
      </b-form-group>    
      <b-form-group>
        <b-form-checkbox
                :checked="isAdmin"
                switch
                @change="toggleAdmin"
        > {{ $t('fields.admin') }} </b-form-checkbox>
      </b-form-group>
      <b-button type="submit" 
                :disabled="errors.any()" 
                :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')"
                @click="validate" 
                >
        {{ $t('actions.submit') }}
      </b-button>
    </b-card>

  </div>
</template>

<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;

  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
